/**
 * The memo's Versions tab (DOP-621): every version of the deal's memo, and
 * what changed between any two of them.
 *
 * Every re-run and every save of a person's edits is a version, and nothing
 * is overwritten. The compare defaults to the version on screen against the
 * one it came from: the version it was copied from for a save, the one
 * before it for a re-run. Clicking a version compares it with the one on
 * screen; Restore copies it, unchanged, as the newest version (DOP-623). Taken-out words are struck through and added words
 * underlined, so the difference never rests on colour alone. Sections with
 * no changes fold into one line.
 */

import { ArrowRight, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { DealScreeningRun, DealScreeningRunVersion } from "../../types/dealScreening";
import { runHref } from "./DealsTable";
import { formatIsoTimestamp } from "./dealScreeningFormat";
import { type SectionDiff, diffRuns } from "./memoDiff";
import { VersionName, versionText } from "./memoVersionLabel";

export interface VersionsViewProps {
	versions: DealScreeningRunVersion[];
	loading: boolean;
	error: Error | undefined;
	/** The version on screen, already loaded. */
	currentRun: DealScreeningRun;
	loadRun: (runId: string) => Promise<DealScreeningRun>;
	/** Copy a version unchanged as the newest one (DOP-623). */
	onRestore?: (versionId: string) => Promise<void>;
	/** This reader's id (shared memos): the "You" filter, and "you" on cards. */
	meId?: string;
	/** Open comparing from this version to the newest (the saved banner's "Show changes"). */
	compareFrom?: number | null;
}

/** "Everyone", "You", each person who saved or ran a version, and "Doppler". */
export function editorChips(
	versions: DealScreeningRunVersion[],
	meId: string | undefined,
): { id: string; label: string }[] {
	const people = new Map<string, string>();
	versions.forEach((v) => {
		const id = v.origin === "edited" ? v.saved_by_user_id : v.generated_by_user_id;
		const name = v.origin === "edited" ? v.saved_by_name : v.generated_by_name;
		if (id && id !== meId && name) people.set(id, name);
	});
	return [
		{ id: "everyone", label: "Everyone" },
		...(meId ? [{ id: meId, label: "You" }] : []),
		...Array.from(people, ([id, label]) => ({ id, label })),
		{ id: "doppler", label: "Doppler" },
	];
}

/** Whether a version is one of the chip's editor's. */
export function byEditor(version: DealScreeningRunVersion, chip: string): boolean {
	if (chip === "everyone") return true;
	if (chip === "doppler") return version.origin !== "edited";
	const ids = [
		version.origin === "edited" ? version.saved_by_user_id : version.generated_by_user_id,
		...(version.contributors ?? []).map((c) => c.user_id),
	];
	return ids.includes(chip);
}

/** "3 changes in Overview, Risks", from a version's contributors. */
const contributorsLine = (version: DealScreeningRunVersion, run: DealScreeningRun, meId: string | undefined): string[] =>
	(version.contributors ?? []).map((c) => {
		const titles = Object.keys(c.sections)
			.map((key) => run.sections.find((s) => s.key === key)?.title.replace(/[*_]/g, "") ?? key)
			.join(", ");
		const who = c.user_id === meId ? "You" : (c.name ?? "Someone");
		return `${who}: ${c.changes === 1 ? "1 change" : `${c.changes} changes`}${titles ? ` in ${titles}` : ""}`;
	});

const unnamedTitle = (version: DealScreeningRunVersion): string =>
	version.origin === "edited" ? "Your edits" : "Written by Doppler";

/** "After CFO call (v3)" for a named version; "v2 · Written by Doppler" otherwise. */
export const versionTitle = (version: DealScreeningRunVersion): string =>
	version.name ? versionText(version) : `v${version.version} · ${unnamedTitle(version)}`;

/** Whether this version carries a deal name other than the deal's current one. */
const differentTitle = (version: DealScreeningRunVersion, current: string): boolean =>
	Boolean(version.deal_name?.trim()) && version.deal_name?.trim() !== current.trim();

const versionMeta = (version: DealScreeningRunVersion, meId?: string): string =>
	[
		version.origin === "edited"
			? version.saved_by_user_id && version.saved_by_user_id === meId
				? "Saved by you"
				: `Saved by ${version.saved_by_name ?? "a person"}`
			: version.generated_by_name
				? `Run by ${version.generated_by_user_id === meId ? "you" : version.generated_by_name}`
				: "Doppler",
		formatIsoTimestamp(version.origin === "edited" ? version.created_at : (version.generated_at ?? version.created_at)),
		version.based_on_version ? `from v${version.based_on_version}` : null,
		version.status === "reviewed" ? "reviewed" : null,
	]
		.filter(Boolean)
		.join(" · ");

/** The version a compare starts from: what this one came from. */
function defaultBase(
	versions: DealScreeningRunVersion[],
	current: DealScreeningRunVersion | undefined,
): DealScreeningRunVersion | undefined {
	if (!current) return undefined;
	if (current.based_on_version) {
		const base = versions.find((v) => v.version === current.based_on_version);
		if (base) return base;
	}
	return [...versions]
		.filter((v) => v.version < current.version && v.generated_at)
		.sort((a, b) => b.version - a.version)[0];
}

const DiffBody: React.FC<{ diff: SectionDiff; editor?: string | null }> = ({ diff, editor = null }) => (
	<p className="whitespace-pre-wrap font-serif text-[15px] leading-[1.7] text-[#1c1d1f]">
		{diff.parts.map((part, index) =>
			part.kind === "same" ? (
				// Parts are positional within one diff.
				// eslint-disable-next-line react/no-array-index-key
				<React.Fragment key={index}>{part.text}</React.Fragment>
			) : part.kind === "added" ? (
				// A person's additions read in their purple, with their name on hover.
				// eslint-disable-next-line react/no-array-index-key
				<ins
					key={index}
					title={editor ?? undefined}
					className={
						editor
							? "rounded-sm bg-[#f4f0ff] text-[#5b3fa8] underline decoration-[#a48ff0] underline-offset-[3px]"
							: "rounded-sm bg-[#ecfdf3] text-[#067647] underline underline-offset-[3px]"
					}
				>
					{part.text}
				</ins>
			) : (
				// eslint-disable-next-line react/no-array-index-key
				<del key={index} className="rounded-sm bg-[#fef3f2] text-[#b42318] line-through">
					{part.text}
				</del>
			),
		)}
	</p>
);

const VersionsView: React.FC<VersionsViewProps> = ({
	versions,
	loading,
	error,
	currentRun,
	loadRun,
	onRestore,
	meId,
	compareFrom = null,
}) => {
	const sorted = useMemo(() => [...versions].sort((a, b) => b.version - a.version), [versions]);
	const [chip, setChip] = useState("everyone");
	const chips = useMemo(() => editorChips(sorted, meId), [sorted, meId]);
	const listed = useMemo(() => sorted.filter((v) => byEditor(v, chip)), [sorted, chip]);
	const current = sorted.find((v) => v.public_id === currentRun.public_id);
	const [afterId, setAfterId] = useState<string | null>(null);
	const [beforeId, setBeforeId] = useState<string | null>(null);
	// The banner's "Show changes": from the version last seen to the newest.
	useEffect(() => {
		if (compareFrom === null) return;
		const from = sorted.find((v) => v.version === compareFrom);
		const to = sorted[0];
		if (!from || !to || from.public_id === to.public_id) return;
		setBeforeId(from.public_id);
		setAfterId(to.public_id);
	}, [compareFrom, sorted]);
	const [changesOnly, setChangesOnly] = useState(true);
	const [runs, setRuns] = useState<Record<string, DealScreeningRun>>({});
	const [loadError, setLoadError] = useState<string | null>(null);
	const [confirmId, setConfirmId] = useState<string | null>(null);
	const [restoring, setRestoring] = useState(false);

	const after = sorted.find((v) => v.public_id === (afterId ?? currentRun.public_id)) ?? current;
	const before =
		sorted.find((v) => v.public_id === beforeId) ?? defaultBase(sorted, after);

	/**
	 * A card clicked is compared with the version on screen, older one on
	 * the left; the on-screen card goes back to what it came from.
	 */
	const compareWith = (version: DealScreeningRunVersion) => {
		if (!current || version.public_id === current.public_id) {
			setBeforeId(null);
			setAfterId(null);
			return;
		}
		const older = version.version < current.version;
		setBeforeId(older ? version.public_id : current.public_id);
		setAfterId(older ? current.public_id : version.public_id);
	};
	/** The card being compared with the one on screen, if any. */
	const comparing = [before, after].find(
		(v) => v && v.public_id !== currentRun.public_id,
	)?.public_id;
	const newest = sorted[0];

	const restore = async (version: DealScreeningRunVersion) => {
		if (!onRestore) return;
		setRestoring(true);
		try {
			await onRestore(version.public_id);
		} finally {
			setRestoring(false);
			setConfirmId(null);
		}
	};

	useEffect(() => {
		const wanted = [before?.public_id, after?.public_id].filter(
			(id): id is string => Boolean(id) && id !== currentRun.public_id && !runs[id as string],
		);
		if (wanted.length === 0) return;
		let cancelled = false;
		void Promise.all(wanted.map((id) => loadRun(id)))
			.then((loaded) => {
				if (cancelled) return;
				setRuns((was) => ({
					...was,
					...Object.fromEntries(loaded.map((run) => [run.public_id, run])),
				}));
				setLoadError(null);
			})
			.catch(() => {
				if (!cancelled) setLoadError("Could not load that version to compare.");
			});
		return () => {
			cancelled = true;
		};
	}, [before?.public_id, after?.public_id, currentRun.public_id, loadRun, runs]);

	const runOf = (id: string | undefined) =>
		id === currentRun.public_id ? currentRun : id ? runs[id] : undefined;
	const beforeRun = runOf(before?.public_id);
	const afterRun = runOf(after?.public_id);
	const diffs = useMemo(
		() => (beforeRun && afterRun ? diffRuns(beforeRun, afterRun) : null),
		[beforeRun, afterRun],
	);
	// Whose changes the comparison shows: the newer version's editor, when a person saved it.
	const editorLabel =
		after?.origin === "edited"
			? `${after.saved_by_user_id && after.saved_by_user_id === meId ? "You" : (after.saved_by_name ?? "A person")} · ${formatIsoTimestamp(after.created_at)}`
			: null;

	if (error) {
		return (
			<p role="alert" className="text-sm text-red-600">
				Could not load the versions: {error.message}
			</p>
		);
	}
	if (loading && versions.length === 0) {
		return <p className="text-sm text-text-muted">Loading versions…</p>;
	}

	const changed = diffs?.filter((d) => d.changes > 0) ?? [];
	const unchanged = diffs?.filter((d) => d.changes === 0) ?? [];
	const total = changed.reduce((sum, d) => sum + d.changes, 0);

	const select = (
		label: string,
		value: string | undefined,
		onChange: (id: string) => void,
	) => (
		<label className="relative inline-flex items-center">
			<span className="sr-only">{label}</span>
			<select
				aria-label={label}
				value={value ?? ""}
				onChange={(event) => onChange(event.target.value)}
				className="h-9 appearance-none rounded-lg border border-gray-300 bg-white py-0 pl-3 pr-8 text-sm font-semibold text-[#111111] focus:outline-none focus:ring-2 focus:ring-[#008782]"
			>
				{sorted.map((v) => (
					<option key={v.public_id} value={v.public_id}>
						{versionTitle(v)}
					</option>
				))}
			</select>
			<ChevronsUpDown className="pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-[#6B7280]" aria-hidden />
		</label>
	);

	return (
		<div className="flex items-start gap-6" data-testid="run-versions">
			<nav aria-label="All versions" className="w-[270px] shrink-0 space-y-1">
				<p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[.09em] text-[#9a9ea3]">
					All versions
				</p>
				{meId !== undefined && chips.length > 2 && (
					<div className="mb-2 flex flex-wrap gap-1 px-2" role="group" aria-label="Versions by">
						{chips.map((option) => (
							<button
								key={option.id}
								type="button"
								aria-pressed={chip === option.id}
								onClick={() => setChip(option.id)}
								className={`h-6 rounded-full border px-2 text-[11.5px] font-medium ${
									chip === option.id
										? "border-[#004C4B] bg-[#E6F3F3] text-[#004C4B]"
										: "border-gray-200 bg-white text-[#5B6472] hover:bg-gray-50"
								}`}
							>
								{option.label}
							</button>
						))}
					</div>
				)}
				<ul className="space-y-1">
					{listed.map((version) => {
						const onScreen = version.public_id === currentRun.public_id;
						const selected = version.public_id === comparing;
						const canRestore =
							Boolean(onRestore) &&
							version.public_id !== newest?.public_id &&
							Boolean(version.generated_at) &&
							!version.generation_error;
						const confirming = confirmId === version.public_id;
						return (
							<li key={version.public_id}>
								<div
									data-testid={`version-card-${version.version}`}
									onClick={(event) => {
										// The card compares; its link and buttons do their own thing.
										if ((event.target as HTMLElement).closest("a,button")) return;
										compareWith(version);
									}}
									className={`flex cursor-pointer gap-2.5 rounded-[10px] border p-3 ${
										selected
											? "border-[#004C4B] bg-white"
											: onScreen
												? "border-[#e7e7e4] bg-white"
												: "border-transparent hover:bg-white/70"
									}`}
								>
									<span
										className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
											version.origin === "edited" ? "bg-[#5B3FA8]" : "bg-[#008782]"
										}`}
										aria-hidden
									/>
									<div className="min-w-0 flex-1">
										<button
											type="button"
											onClick={() => compareWith(version)}
											aria-pressed={selected}
											className="text-left text-[13.5px] font-semibold text-[#111111] focus:outline-none focus-visible:underline"
										>
											<VersionName
												version={version.version}
												name={version.name}
												fallback={unnamedTitle(version)}
											/>
										</button>
										<p className="mt-0.5 text-xs leading-snug text-[#6B7280]">
											{version.generation_error ?? versionMeta(version, meId)}
										</p>
										{contributorsLine(version, currentRun, meId).map((line) => (
											<p key={line} className="mt-0.5 text-xs leading-snug text-[#5B3FA8]" data-testid="version-contributor">
												{line}
											</p>
										))}
										{/* A version keeps the deal name it was saved with (DOP-659):
										    said when it is not the deal's name today, since a restore
										    brings it back. */}
										{differentTitle(version, currentRun.deal.name) && (
											<p
												className="mt-0.5 truncate text-xs leading-snug text-[#6B7280]"
												data-testid={`version-title-${version.version}`}
											>
												Titled “{version.deal_name}”
											</p>
										)}
										{confirming ? (
											<div className="mt-2 rounded-lg border border-[#e5e7eb] bg-[#fafaf8] p-2.5">
												<p className="text-xs leading-snug text-[#3f4247]">
													Make a copy of v{version.version} the newest version (v
													{(newest?.version ?? version.version) + 1})? Every other version stays as it is.
												</p>
												<div className="mt-2 flex gap-2">
													<button
														type="button"
														disabled={restoring}
														onClick={() => void restore(version)}
														className="h-7 rounded-md bg-[#004C4B] px-2.5 text-xs font-semibold text-white hover:bg-[#003a3a] disabled:opacity-60"
													>
														{restoring ? "Restoring…" : `Restore v${version.version}`}
													</button>
													<button
														type="button"
														disabled={restoring}
														onClick={() => setConfirmId(null)}
														className="h-7 rounded-md border border-[#e5e7eb] bg-white px-2.5 text-xs font-medium text-[#1F2937]"
													>
														Cancel
													</button>
												</div>
											</div>
										) : (
											<div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium">
												{onScreen ? (
													<span className="text-[#004C4B]">On screen</span>
												) : (
													<Link
														to={runHref(version.public_id, "memo")}
														className="text-[#004C4B] hover:underline"
													>
														Open
													</Link>
												)}
												{canRestore && (
													<button
														type="button"
														onClick={() => setConfirmId(version.public_id)}
														className="text-[#004C4B] hover:underline"
													>
														Restore
													</button>
												)}
												{selected && <span className="text-[#6B7280]">Comparing</span>}
											</div>
										)}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
				<p className="mt-3 rounded-[10px] border border-dashed border-[#c9ccd1] p-3 text-[12.5px] leading-relaxed text-[#5B6472]">
					Click a version to compare it with the one on screen. Every save, re-run and
					restore makes a new version; nothing is overwritten.
				</p>
			</nav>

			<section aria-label="Compare versions" className="min-w-0 flex-1">
				{sorted.length < 2 ? (
					<p className="rounded-xl border border-gray-200 bg-white px-5 py-8 text-center text-sm text-[#5B6472]">
						This is the only version so far. Edit the memo and save, or write a new
						version, to compare two.
					</p>
				) : (
					<>
						<div className="flex flex-wrap items-center gap-2.5 pb-4">
							<span className="text-sm text-[#5B6472]">Compare</span>
							{select("Older version", before?.public_id, setBeforeId)}
							<ArrowRight className="h-4 w-4 text-[#6B7280]" aria-hidden />
							{select("Newer version", after?.public_id, setAfterId)}
							{diffs && (
								<span className="text-sm text-[#5B6472]">
									{total === 0
										? "No changes"
										: `${total === 1 ? "1 change" : `${total} changes`} in ${
												changed.length === 1 ? "1 section" : `${changed.length} sections`
											}`}
								</span>
							)}
							<div className="ml-auto inline-flex gap-0.5 rounded-lg bg-[#f2f2f2] p-[3px]" role="group" aria-label="Show">
								{[
									{ on: true, label: "Changes only" },
									{ on: false, label: "Whole memo" },
								].map((option) => (
									<button
										key={option.label}
										type="button"
										aria-pressed={changesOnly === option.on}
										onClick={() => setChangesOnly(option.on)}
										className={`h-7 rounded-md px-3 text-sm ${
											changesOnly === option.on
												? "bg-white font-semibold text-[#111111] shadow-sm"
												: "text-[#5B6472]"
										}`}
									>
										{option.label}
									</button>
								))}
							</div>
						</div>

						{loadError && (
							<p role="alert" className="pb-3 text-sm text-red-600">
								{loadError}
							</p>
						)}
						{!diffs ? (
							<p className="text-sm text-[#5B6472]">Loading both versions…</p>
						) : (
							<article className="space-y-5 rounded-xl border border-[#e7e7e4] bg-white px-8 py-6">
								<p className="flex flex-wrap gap-4 text-[12.5px] text-[#5B6472]">
									<span>
										<del className="bg-[#fef3f2] px-1 text-[#b42318] line-through">struck through</del> = taken out
									</span>
									<span>
										{editorLabel ? (
											<ins className="bg-[#f4f0ff] px-1 text-[#5b3fa8] underline">underlined</ins>
										) : (
											<ins className="bg-[#ecfdf3] px-1 text-[#067647] underline">underlined</ins>
										)}{" "}
										= added{editorLabel ? ` by ${editorLabel}` : ""}
									</span>
								</p>
								{(changesOnly ? changed : diffs).map((diff) => (
									<div key={diff.key} data-testid={`diff-${diff.key}`}>
										<h3 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[.08em] text-[#004C4B]">
											{diff.title}
											<span className="rounded-full bg-[#f2f2f2] px-2 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-[#5B6472]">
												{diff.changes === 0
													? "no changes"
													: diff.changes === 1
														? "1 change"
														: `${diff.changes} changes`}
											</span>
										</h3>
										<DiffBody diff={diff} editor={editorLabel} />
									</div>
								))}
								{changesOnly && unchanged.length > 0 && (
									<p className="rounded-lg border border-dashed border-gray-200 bg-[#fafafa] px-3.5 py-2.5 text-sm text-[#6B7280]">
										No changes in: {unchanged.map((d) => d.title).join(", ")}
									</p>
								)}
							</article>
						)}
					</>
				)}
			</section>
		</div>
	);
};

export default VersionsView;
